import type { ZFilterGroup } from "src/business/queryBuilder/types/ZFilterApiTypes";
import type { ZFilterSearchSortItem } from "src/business/queryBuilder/types/ZFilterSearchTypes";
import { buildFilterSearchPayload } from "src/business/queryBuilder/utils/buildFilterSearchPayload";
import { getApiUrl } from "src/common/api/getUrl";
import { dataParser } from "src/common/dataParser";
import { rest } from "src/common/rest";
import { RtLoaderFn } from "src/components/ReactiveTable/RtLoader";
import { RtRequest } from "src/components/ReactiveTable/RtRequest";
import { RtResponse } from "src/components/ReactiveTable/RtResponse";
import { z } from "zod";

import { ZSpecification } from "../specification";
import { ZClsNode, zClsNode } from "./ClassesTree/ZClsNode";
import { makeClassRow } from "./makeClassRow";
import {
  ClassesFilter,
  ClassParentsFilter,
  zClassCommon,
  ZClassRow,
  ZClassUnsafe,
  zClassUnsafe,
} from "./ZClassRow";
import { ZClassUpdate } from "./ZClassUpdate";

const urlClasses = (path: string = "") => getApiUrl(`/core/classes${path}`);
const urlClassesV1 = (path: string = "") =>
  getApiUrl(`/v1/core/classes${path}`);

type ClassSearchRequest = {
  query?: string;
  filters: ZFilterGroup[];
  tree?: {
    classifierId?: string;
    parentId?: string;
    catalogId?: string;
  };
  page: {
    size: number;
    offset: number;
  };
  sort?: ZFilterSearchSortItem[];
  catalogId?: string;
  classifierId?: string;
};

export const searchClasses = async (
  data: ClassSearchRequest,
): Promise<RtResponse<ZClassUnsafe>> => {
  const resp = await rest.post(urlClassesV1(`/search`), data);
  const zResult = z.object({
    content: zClassUnsafe.array(),
    pagination: z.object({
      totalElements: z.number(),
    }),
  });
  const parsed = dataParser(() => zResult.parse(resp.data), "searchClasses");
  return {
    rows: parsed.content,
    totalRows: parsed.pagination.totalElements,
  };
};

// https://mdm2-dev-magnit.d4r.int/api/swagger-ui/index.html#/Class%20API/getAll_7
export const classesLoader2 =
  (rootParentId: string, schemes: ZSpecification[]) =>
  async (request: RtRequest<ClassesFilter>): Promise<RtResponse<ZClassRow>> => {
    const searchResp = await searchClasses({
      query: request.filters?.query,
      classifierId: rootParentId,
      tree: {
        classifierId: rootParentId,
        parentId: request.filters?.parentId,
      },
      ...buildFilterSearchPayload(request),
    });
    return {
      rows: searchResp.rows.map((unsafeRow) =>
        makeClassRow(unsafeRow, schemes),
      ),
      totalRows: searchResp.totalRows,
    };
  };

export const availableParentsLoader =
  (
    id?: string,
    parentId?: string,
  ): RtLoaderFn<ZClassUnsafe, ClassParentsFilter> =>
  async (request) => {
    const url = urlClasses("/available-parents");
    const params = {
      ...request.filters,
      id,
      parentId,
      page: request.page,
      size: request.pageSize,
    };
    const zResult = z.object({
      content: zClassUnsafe.array(),
      pagination: z.object({
        totalElements: z.number(),
      }),
    });
    const resp = await rest.get(url, { params });
    const { content, pagination } = dataParser(
      () => zResult.parse(resp.data),
      url,
    );
    return { rows: content, totalRows: pagination.totalElements };
  };

export const loadClassUnsafe = async (id: string): Promise<ZClassUnsafe> => {
  const resp = await rest.get(urlClassesV1(`/${id}`));
  return dataParser(() => zClassUnsafe.parse(resp.data), "loadClassUnsafe");
};

export const loadClassSafe = async (
  id: string,
  schemes: ZSpecification[],
): Promise<ZClassRow> => {
  const unsafe = await loadClassUnsafe(id);
  return makeClassRow(unsafe, schemes);
};

// TODO: К сожалению, в api есть баг - при создании класса не присылается значение displayId, что даёт ошибку парсинга zClassRow
// Поэтому решено изменить тип результата

const zClassResult = zClassCommon.pick({ id: true, name: true });
export type ZClassResult = z.infer<typeof zClassResult>;

export const createClass = async (
  parentId: string,
  data: ZClassUpdate,
): Promise<ZClassResult> => {
  const resp = await rest.post(urlClasses(`/${parentId}`), data);
  return dataParser(() => zClassResult.parse(resp.data), "createClass");
};

export const updateClass = async (
  classId: string,
  data: ZClassUpdate,
): Promise<ZClassUnsafe> => {
  const resp = await rest.put(urlClasses(`/${classId}`), data);
  return dataParser(() => zClassUnsafe.parse(resp.data), "updateClass");
};

export const deleteClass = async (classId: string): Promise<void> => {
  await rest.delete(urlClasses(`/${classId}`));
};

export const loadClsBranch = async (parentId: string): Promise<ZClsNode[]> => {
  const url = getApiUrl(`/core/tree/${parentId}/children`);
  const resp = await rest.get(url);
  return dataParser(() => zClsNode.array().parse(resp.data), "loadClsBranch");
};
